import { flattenNavigation, navigationTrail } from './model';

export type NavigationFeature = 'shell' | 'environmentsV2' | 'portalsWorkspace';
export interface NavigationContext {
  pathname: string;
  search: string;
  features: Readonly<Record<NavigationFeature, boolean>>;
}
export interface NavigationDestination {
  label: string;
  /** An existing App route, optionally with a query actually consumed by its page. */
  href: string;
  source: string;
  feature?: NavigationFeature;
  note?: string;
}
export interface NavigationBinding {
  destinations: readonly NavigationDestination[];
  /** Explains incomplete proposed functionality without disguising a legacy page as it. */
  limitation?: string;
}
export type ResolvedDestination = NavigationDestination & (
  { availability: 'link'; to: string } |
  { availability: 'context-required' | 'unavailable'; reason: string }
);
const route = (href: string, label: string, feature?: NavigationFeature, note?: string): NavigationDestination =>
  ({ href, label, feature, note, source: 'dashboard/src/App.tsx' });
const recovery = (view: string, label: string, extra = ''): NavigationDestination =>
  ({ ...route(`/backups?recovery_view=${view}${extra}`, label), source: 'dashboard/src/pages/recovery/RecoveryConsole.tsx' });
const tab = (value: string, label: string): NavigationDestination =>
  ({ ...route(`/backups?tab=${value}`, label), source: 'dashboard/src/pages/BackupsHub.tsx', note: 'The page retains its existing access and scope checks.' });
const env = (suffix: string, label: string) => route(`/environments/:environmentId/${suffix}`, label, 'environmentsV2');
const portal = (suffix: string, label: string, workspace = false) => route(`/portals/:portalId/${suffix}`, label, workspace ? 'portalsWorkspace' : undefined);
const release = (suffix: string, label: string) => route(`/releases/:releaseId/${suffix}`, label, 'environmentsV2');
const bind = (...destinations: NavigationDestination[]): NavigationBinding => ({ destinations });
const partial = (limitation: string, ...destinations: NavigationDestination[]): NavigationBinding => ({ destinations, limitation });

/** Exact approved node identities; missing bindings are an error, never a guessed URL. */
export const navigationBindings: Readonly<Record<string, NavigationBinding>> = {
  'root-01': bind(route('/home?view=overview', 'Environment overview', 'shell'), route('/home', 'Home', 'shell'), route('/', 'Dashboard')),
  '01-01': bind(route('/home?view=overview', 'Environment overview', 'shell'), route('/home?view=estate-health', 'Estate health', 'shell'), route('/environments', 'Environment directory')),
  '01-02': bind(route('/operations/attention', 'Attention'), route('/home?view=attention', 'Home attention', 'shell')),
  '01-03': bind(route('/home?view=recent-activity', 'Recent activity', 'shell'), route('/operations/jobs', 'Recent jobs'), route('/monitor/activity', 'Ingestion activity')),
  'root-02': bind(route('/usage-cost/summary', 'AWS spend summary')),
  '02-01': bind(route('/usage-cost/summary', 'AWS spend summary'), route('/administration/commercial/cost-explorer', 'AWS Cost Explorer'), route('/billing', 'Existing hosting costs and pricing hub')),
  '02-01-01': bind(route('/usage-cost/trajectory', 'Spend trajectory')),
  '02-01-02': bind(route('/usage-cost/ai-roles', 'AI role usage')),
  '02-01-03': bind(route('/usage-cost/investigations', 'Commercial investigations')),
  '02-02': bind(route('/administration/commercial/budgets', 'Budgets')),
  '02-06': bind(route('/usage-cost/allocation', 'AWS spend allocation')),
  '02-06-01': bind(route('/usage-cost/allocation/rules', 'Allocation rules')),
  '02-06-02': bind(route('/administration/commercial/metering', 'Commercial metering')),
  'root-03': bind(route('/backups', 'Recovery workspace')),
  '03-01': bind(recovery('overview', 'Recovery overview')),
  '03-02': bind(recovery('catalogue', 'Snapshot catalogue')),
  '03-02-01': bind(recovery('catalogue', 'Portal snapshot catalogue')),
  '03-02-02': bind(tab('catalogue', 'Native recovery point catalogue')),
  '03-02-03': bind(recovery('catalogue', 'Compare two recovery points', '&recovery_compare_open=1')),
  '03-03': bind(recovery('capture', 'Prepare a backup')),
  '03-04': bind(recovery('recover', 'Prepare a restore')),
  '03-04-01': bind(recovery('recover', 'Portal or environment restore', '&recovery_kind=restore')),
  '03-04-02': bind(recovery('recover', 'Reactivate project', '&recovery_kind=project_reactivate'), recovery('recover', 'Reconstruct project', '&recovery_kind=project_reconstruct')),
  '03-04-03': partial('Prepared plans are selected within the recovery workspace.', recovery('recover', 'Restore plans')),
  '03-05': bind(recovery('prepare', 'Prepare test data reset')),
  '03-05-01': bind(recovery('prepare', 'Baseline and scenario preparation'), route('/operations/demo/scenarios', 'Scenario catalogue')),
  '03-05-02': bind(route('/operations/demo/resets', 'Reset journal'), recovery('history', 'Project preparation')),
  '03-06': bind(recovery('configure', 'Recovery configuration')),
  '03-06-01': partial('Recovery schedules remain inside recovery configuration.', recovery('configure', 'Recovery schedule configuration')),
  '03-06-02': bind(recovery('coverage', 'Resource inventory')),
  '03-06-03': bind(tab('vaults', 'Vaults and disaster recovery')),
  '03-07': bind(recovery('monitor', 'Recovery operation monitoring'), tab('evidence', 'Recovery evidence')),
  '03-07-01': bind(recovery('monitor', 'Recovery operations')),
  '03-07-02': bind(tab('rehearsals', 'Rehearsals'), recovery('validate', 'Validation')),
  '03-07-03': bind(tab('demo-readiness', 'Demo readiness'), tab('demo-evidence', 'Demo evidence'), route('/operations/demo/readiness', 'Operations demo readiness'), route('/operations/demo/evidence', 'Operations demo evidence')),
  'root-04': bind(route('/portals', 'Portals')),
  '04-01': bind(route('/portals', 'Portal directory')),
  '04-02': bind(portal('overview', 'Selected portal overview', true), route('/portals', 'Select a portal')),
  '04-02-01': bind(portal('overview', 'Overview', true), portal('environments', 'Environments', true), portal('deployments', 'Deployments', true)),
  '04-02-02': bind(portal('configuration/effective', 'Effective configuration', true), portal('configuration/general', 'General configuration'), portal('configuration/ai-models', 'AI models'), portal('configuration/ai-models/compare', 'Compare AI models'), portal('configuration/plans-pricing', 'Plans and pricing'), portal('configuration/integrations', 'Integrations'), portal('settings', 'Portal settings')),
  '04-02-03': bind(portal('sources', 'Data sources and mappings'), route('/operations/data-flows/sources', 'Source directory')),
  '04-02-04': bind(portal('access/users', 'Portal users'), portal('access/organisations', 'Portal organisations'), portal('access/invitations', 'Portal invitations'), portal('access/service-identities', 'Portal service identities')),
  '04-02-05': bind(portal('history', 'History', true), portal('documentation', 'Documentation', true)),
  'root-05': bind(route('/environments', 'Environments')),
  '05-01': bind(route('/environments', 'Environment directory'), route('/environments/new', 'Plan an environment', 'environmentsV2')),
  '05-02': bind(env('overview', 'Environment overview'), env('services', 'Environment services'), env('e06', 'Environment service detail'), route('/monitor', 'Platform monitor')),
  '05-03': bind(env('resources', 'Environment resources')),
  '05-04': bind(env('data', 'Environment data baselines'), env('operations/demo', 'Demo data state')),
  '05-05': bind(env('lifecycle', 'Environment lifecycle')),
  '05-06': bind(route('/environments/compare', 'Compare environments')),
  '05-07': bind(env('operations/verification', 'Environment verification'), env('operations/recovery', 'Environment recovery')),
  'root-06': bind(route('/operations', 'Command centre')),
  '06-01': bind(route('/operations/jobs', 'Jobs'), route('/monitor/activity', 'Ingestion activity'), env('operations/activity', 'Environment activity'), env('operations', 'Environment operations')),
  '06-01-01': bind(route('/operations/jobs', 'Operations jobs'), route('/operations/data-flows/jobs', 'Data flow jobs'), route('/ingestion/jobs', 'Ingestion jobs'), route('/ingestion/create', 'Create ingestion job')),
  '06-01-02': bind(route('/operations/approvals', 'Operational approvals')),
  '06-01-03': bind(route('/operations/demo/holds', 'Environment holds')),
  '06-01-04': bind(route('/operations/logs', 'Logs'), route('/operations/audit', 'Audit evidence'), route('/logs', 'Legacy logs link')),
  '06-02': bind(route('/operations/schedules', 'Operations schedules'), route('/operations/data-flows/schedules', 'Data flow schedules'), route('/ingestion?tab=schedules', 'Ingestion schedules')),
  '06-03': bind(route('/operations/incidents', 'Incidents'), route('/operations/attention', 'Attention')),
  '06-03-01': bind(route('/operations/incidents', 'Incidents')),
  '06-03-02': bind(route('/home?view=alerts', 'Alerts', 'shell')),
  '06-03-03': bind(route('/support', 'Support tickets')),
  '06-04': bind(route('/operations/data-flows', 'Data flows'), route('/ingestion', 'Ingestion')),
  '06-04-01': bind(route('/operations/data-flows/sources', 'Sources'), route('/datastores', 'Data stores'), route('/ingestion?tab=sources', 'Ingestion sources'), route('/aemo', 'AEMO sources'), route('/energy-organisations', 'Energy organisations'), route('/operations/data-flows/activity', 'AEMO activity')),
  '06-04-02': bind(route('/ingestion?tab=pipelines', 'Pipelines'), route('/ingestion/control', 'Ingestion control')),
  '06-04-03': bind(route('/operations/data-flows/taxonomies', 'Taxonomy source availability')),
  '06-05': bind(route('/monitor', 'Platform monitor'), route('/services', 'Platform services')),
  '06-05-01': bind(route('/monitor', 'Live metrics')),
  '06-05-02': bind(route('/services', 'Platform services')),
  '06-05-03': bind(route('/developer-vms', 'Developer VMs')),
  'root-07': bind(route('/releases', 'Releases')),
  '07-01': bind(route('/releases', 'Release list'), route('/releases/new', 'Create release', 'environmentsV2')),
  '07-02': bind(route('/promotion-packages', 'Promotion packages'), route('/releases/promote', 'Promote releases', 'environmentsV2')),
  '07-03': bind(release('summary', 'Selected release summary'), route('/releases', 'Select a release')),
  '07-03-01': bind(release('summary', 'Summary'), release('changeset', 'Changes')),
  '07-03-02': bind(release('gates', 'Gates'), release('approvals', 'Approvals')),
  '07-03-03': bind(release('rollout', 'Rollout'), release('verification', 'Verification')),
  '07-03-04': bind(release('rollback', 'Rollback')),
  '07-03-05': bind(release('history', 'History')),
  '07-04': bind(route('/releases/artifacts/configuration-baselines', 'Release artifacts', 'environmentsV2')),
  '07-04-01': bind(route('/releases/artifacts/configuration-baselines', 'Configuration baselines', 'environmentsV2')),
  '07-04-02': bind(route('/releases/artifacts/data-snapshots', 'Data snapshot references', 'environmentsV2')),
  '07-04-03': bind(route('/releases/artifacts/run-profiles', 'Run profiles', 'environmentsV2')),
  '07-04-04': bind(route('/releases/artifacts/provenance', 'Provenance', 'environmentsV2')),
  '07-05': bind(route('/releases/deployments', 'Deployments'), portal('deployments', 'Selected portal deployments', true)),
  'root-08': bind(route('/customers', 'Customers'), route('/crm', 'CRM workspace')),
  '08-01': bind(route('/customers/organisations', 'Organisations'), route('/organisations', 'Legacy organisations')),
  '08-02': bind(route('/customers/contacts', 'Contacts'), route('/contacts/people', 'Contact directory')),
  '08-03': bind(route('/customers/interactions', 'Interactions')),
  '08-04': bind(route('/customers/documents', 'Documents'), route('/customers/sharing', 'Sharing'), route('/crm/documents', 'CRM documents'), route('/crm/sharing', 'CRM sharing')),
  '08-05': bind(route('/customers/subscriptions', 'Subscription source availability')),
  '08-06': partial('Existing commercial pricing and budgets are separate from AWS implementation budget enforcement.', route('/billing?tab=pricing', 'Pricing hub'), route('/pricing', 'Pricing configuration'), route('/administration/commercial/pricing', 'Commercial pricing'), route('/administration/commercial/budgets', 'Existing commercial budgets')),
  '08-07': bind(route('/customers/support', 'Customer support'), route('/customers/access-projection', 'Access projections')),
  'root-09': bind(route('/administration', 'Administration')),
  '09-01': bind(route('/access', 'Access control'), route('/users-orgs', 'Users and organisations')),
  '09-01-01': bind(route('/administration/identity/users', 'Identity users'), route('/administration/identity/invitations', 'Invitations'), route('/access/users', 'Access users'), route('/portal-users', 'Portal users')),
  '09-01-02': bind(route('/administration/identity/access', 'Access and roles'), route('/access', 'Access control'), env('access', 'Environment access')),
  '09-01-03': bind(route('/administration/identity/reviews', 'Access reviews')),
  '09-01-04': bind(route('/administration/identity/providers', 'Identity providers')),
  '09-01-05': bind(route('/administration/identity/service-identities', 'Service identities')),
  '09-01-06': bind(route('/administration/security/posture', 'Security posture'), route('/administration/security/policies', 'Security policies')),
  '09-01-07': bind(route('/administration/security/keys', 'Security keys'), route('/key-management', 'Encryption keys')),
  '09-01-08': bind(route('/administration/security/break-glass', 'Emergency access')),
  '09-02': bind(route('/administration/platform/registries', 'Platform registries'), route('/settings', 'Personal settings')),
  '09-02-01': bind(route('/administration/platform/registries', 'Registries'), route('/administration/platform/environment-templates', 'Environment templates')),
  '09-02-02': bind(route('/administration/platform/feature-flags', 'Feature flags')),
  '09-02-03': bind(route('/administration/platform/confidence-gates', 'Confidence gates')),
  '09-02-04': bind(route('/administration/platform/managed-writers', 'Managed writers')),
  '09-02-05': bind(route('/administration/developer-workspaces', 'Developer workspaces')),
  '09-03': bind(route('/administration/integrations', 'Integrations')),
  '09-04': bind(route('/administration/ai-model-defaults', 'Platform AI model defaults'), portal('configuration/ai-models', 'Selected portal AI models')),
  'root-10': bind(route('/knowledge', 'Knowledge')),
  '10-01': bind(route('/knowledge', 'Browse knowledge'), route('/search', 'Search', 'shell')),
  '10-02': bind(route('/knowledge/architecture', 'Architecture')),
  '10-02-01': bind(route('/knowledge/architecture', 'Architecture views')),
  '10-02-02': bind(route('/knowledge/decisions', 'Decisions and ADRs')),
  '10-02-03': bind(route('/knowledge/diagrams', 'Diagrams')),
  '10-03': bind(route('/knowledge/data-catalogue', 'Data catalogue')),
  '10-04': bind(route('/knowledge/runbooks', 'Operations guidance')),
  '10-04-01': bind(route('/knowledge/runbooks', 'Runbooks')),
  '10-04-02': bind(route('/knowledge/guides', 'User guides')),
  '10-04-03': bind(route('/knowledge/help-records', 'Help records')),
  '10-05': bind(route('/knowledge/evidence', 'Design evidence')),
  '10-05-01': bind(route('/knowledge/evidence', 'Evidence and pack status')),
  '10-05-02': bind(route('/knowledge/exports', 'Exports')),
};

export function validateNavigationBindings(): void {
  const ids = new Set(flattenNavigation().map(entry => entry.node.id));
  if (Object.keys(navigationBindings).length !== ids.size) throw new Error('Navigation binding count differs from the approved model');
  for (const id of ids) {
    const binding = navigationBindings[id];
    if (!binding || (!binding.destinations.length && !binding.limitation)) throw new Error(`Missing navigation binding: ${id}`);
    for (const destination of binding.destinations) {
      if (!destination.href.startsWith('/') || destination.href.startsWith('//') || /[\\\r\n]/.test(destination.href)) throw new Error(`Unsafe navigation binding: ${id}`);
    }
  }
}
validateNavigationBindings();

/** Parameters come only from a matching, existing entity route. Query scope is not an entity selection. */
export function entityContext(pathname: string): Readonly<Record<string, string>> {
  const patterns: [string, RegExp][] = [
    ['portalId', /^\/portals\/([^/]+)\/(?:select|overview|environments|deployments|configuration\/(?:effective|general|ai-models(?:\/compare)?|plans-pricing|integrations)|sources|documentation|history|settings|access\/(?:users|organisations|invitations|service-identities))\/?$/],
    ['environmentId', /^\/environments\/([^/]+)\/(?:overview|resources|services|e06|data|releases|operations(?:\/(?:demo|recovery|verification|activity))?|cost|access|lifecycle|drift)\/?$/],
    ['releaseId', /^\/releases\/([^/]+)\/(?:summary|changeset|gates|approvals|rollout|verification|rollback|history)\/?$/],
  ];
  for (const [key, pattern] of patterns) {
    const match = pattern.exec(pathname);
    if (match) {
      try {
        const value = decodeURIComponent(match[1]);
        if (value && !/[\x00-\x1f]/.test(value)) return { [key]: value };
      } catch { /* malformed route remains context-required */ }
    }
  }
  return {};
}

/** Carry only scope intent, including malformed duplicates so the scope resolver can reject them.
 * Selection filters, drawers and IDs never leak to another destination. No default scope is invented. */
export function withScopeIntent(href: string, search: string): string {
  const [pathname, query = ''] = href.split('?');
  const next = new URLSearchParams(query);
  const current = new URLSearchParams(search);
  for (const key of ['environmentScope', 'environmentId', 'env']) {
    if (!next.has(key)) for (const value of current.getAll(key)) next.append(key, value);
  }
  return pathname + (next.size ? `?${next.toString()}` : '');
}

export function resolveDestinations(id: string, context: NavigationContext): ResolvedDestination[] {
  const binding = navigationBindings[id];
  if (!binding) throw new Error(`Unknown navigation identity: ${id}`);
  const entities = entityContext(context.pathname);
  return binding.destinations.map(destination => {
    if (destination.feature && !context.features[destination.feature]) {
      return { ...destination, availability: 'unavailable', reason: 'This destination is not enabled in the current application configuration.' };
    }
    let missing = '';
    const href = destination.href.replace(/:([A-Za-z]+)/g, (_, key: string) => {
      if (!entities[key]) { missing = key; return ''; }
      return encodeURIComponent(entities[key]);
    });
    if (missing) return { ...destination, availability: 'context-required', reason: `Select a ${missing === 'portalId' ? 'portal' : missing === 'releaseId' ? 'release' : 'registered environment'} from its directory first.` };
    const scoped = withScopeIntent(href, context.search);
    const [path, query = ''] = scoped.split('?');
    const params = new URLSearchParams(query); params.set('screenId', id);
    return { ...destination, availability: 'link', to: path + '?' + params.toString() };
  });
}

/** Query equality uses complete values, never substring matching (e.g. recover != recovery). */
export function destinationMatches(href: string, pathname: string, search: string): boolean {
  const [path, query = ''] = href.split('?');
  if (path !== pathname.replace(/\/$/, '') && !(path === '/' && pathname === '/')) return false;
  const expected = new URLSearchParams(query), actual = new URLSearchParams(search);
  for (const [key, value] of expected) if (actual.getAll(key).length !== 1 || actual.get(key) !== value) return false;
  return true;
}

export function activeNavigationId(context: NavigationContext): string | null {
  const requested = new URLSearchParams(context.search).getAll('screenId');
  if (requested.length === 1 && navigationBindings[requested[0]]) {
    const exact = resolveDestinations(requested[0], context).some(destination => {
      if (destination.availability !== 'link') return false;
      const path = destination.to.split('?')[0];
      const query = destination.href.split('?')[1];
      return destinationMatches(path + (query ? '?' + query : ''), context.pathname, context.search);
    });
    if (exact) return requested[0];
  }
  // Shared contextual links do not steal the identity of their owning workspace.
  const preferred: Readonly<Record<string, string>> = {
    '/datastores': '06-04-01', '/operations/jobs': '06-01-01', '/monitor/activity': '06-01',
    '/operations/data-flows/sources': '06-04-01', '/operations/attention': '01-02',
    '/usage-cost/allocation': '02-06', '/backups?recovery_view=catalogue': '03-02-01',
    '/backups?recovery_view=recover': '03-04', '/backups?recovery_view=prepare': '03-05',
    '/backups?recovery_view=configure': '03-06', '/backups?recovery_view=monitor': '03-07-01',
    '/releases/artifacts/configuration-baselines': '07-04-01',
    '/knowledge': '10-01', '/knowledge/architecture': '10-02-01',
    '/knowledge/runbooks': '10-04-01', '/knowledge/evidence': '10-05-01',
  };
  let winner: { id: string; score: number } | null = null;
  for (const { node, depth } of flattenNavigation()) {
    for (const destination of resolveDestinations(node.id, context)) {
      if (destination.availability !== 'link') continue;
      // Scope does not distinguish menu destinations.
      const unscoped = destination.to.split('?')[0] + (destination.href.includes('?') ? `?${destination.href.split('?')[1]}` : '');
      if (!destinationMatches(unscoped, context.pathname, context.search)) continue;
      const score = new URLSearchParams(destination.href.split('?')[1]).size * 100 + (destination.href.startsWith('/backups?tab=') ? 1000 : 0) + (preferred[destination.href] === node.id ? 10 : 0) + depth;
      if (!winner || score > winner.score) winner = { id: node.id, score };
    }
  }
  return winner?.id ?? null;
}

export function approvedBreadcrumb(context: NavigationContext): { label: string; to?: string }[] | null {
  const id = activeNavigationId(context);
  if (!id) return null;
  const trail = navigationTrail(id);
  return trail.map((node, index) => {
    const destination = resolveDestinations(node.id, context).find(item => item.availability === 'link');
    return { label: node.label, ...(index < trail.length - 1 && destination?.availability === 'link' ? { to: destination.to } : {}) };
  });
}
